import React, { useState, useEffect, useRef } from 'react';
import { supportedJobs } from './SupportedJobs';
import '../styles/modal.css';

const StreamlineModal = ({ isOpen, onClose, onSubmit, setFile, file, betaTesting, setBetaInfo, betaInfo }) => {
  const [jobTitle, setJobTitle] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const MAX_FILE_SIZE = 2.5 * 1024 * 1024; // 2.5 MB
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!isOpen) return null;


  const handleBetaInfoChange = (e) => {
    const { name, value } = e.target;
    setBetaInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleJobTitleChange = (e) => {
    const value = e.target.value;
    setJobTitle(value);

    // Filter suggestions based on input
    if (value.trim() !== '') {
      const filtered = supportedJobs.filter(job =>
        job.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 10); // Limit to 10 suggestions
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setJobTitle(suggestion);
    setShowSuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate inputs
    if (!file) {
      alert("Please upload a resume file");
      return;
    }

    // Validate that job title is one of the supported jobs
    const isValidJobTitle = supportedJobs.some(job =>
      job.toLowerCase() === jobTitle.toLowerCase()
    );

    if (!isValidJobTitle) {
      alert("Please select a valid job title from the suggestions. The job title must match one of our supported positions.");
      return;
    }

    // Create the form data
    const formData = new FormData();
    formData.append('resume', file);
    formData.append('jobTitle', jobTitle);

    // Add beta info directly to the FormData
    if (betaTesting) {
      formData.append('promoCode', betaInfo.promoCode);
      formData.append('email', betaInfo.email);
    }

    // Debug output
    console.log("Form data being sent:", {
      hasFile: !!file,
      jobTitle,
      betaTesting,
      betaInfo
    });

    // Call onSubmit directly with the form data
    onSubmit(formData);
  };

  return (
    <div className="modal-container">
      <div className="modal-card">
        <h2>Streamline Resume</h2>
        <p className="modal-subtitle">Provide your resume and target job details</p>

        <form onSubmit={handleSubmit}>
          <div className="modal-form-section">
            <label>Upload Resume</label>
            <div className="file-upload-container">
              <button
                type="button"
                className="file-upload-button"
                onClick={() => document.getElementById('tailor-file-input').click()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Choose File
              </button>
              <span className="file-name">{file ? file.name : "No File Chosen"}</span>
              <input
                type="file"
                id="tailor-file-input"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  if (file.size > MAX_FILE_SIZE) {
                    alert(`File is too large. Max allowed size is ${MAX_FILE_SIZE / (1024 * 1024)} MB.`);
                    e.target.value = ""; // clears the file input
                    return;
                  }

                  setFile(file); // safe to keep
                }}
                required={!file}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <div className="modal-form-section">
            <label>Job Title</label>
            <div className="modal-autocomplete-container" style={{ position: 'relative' }}>
              <input
                type="text"
                className="modal-autocomplete-input"
                placeholder="Enter target job title"
                value={jobTitle}
                onChange={handleJobTitleChange}
                onFocus={() => jobTitle && setSuggestions(supportedJobs.filter(job =>
                  job.toLowerCase().includes(jobTitle.toLowerCase())
                ).slice(0, 5))}
                required
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul
                  ref={suggestionsRef}
                  className="suggestions-list"
                >
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      onClick={() => selectSuggestion(suggestion)}
                      className="suggestion-item"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {betaTesting && (
            <div className="beta-testing-section">
              <h3>Beta Testing Access</h3>
              <div className="beta-fields-container">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={betaInfo.email}
                    onChange={handleBetaInfoChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Promo Code</label>
                  <input
                    type="text"
                    name="promoCode"
                    placeholder="Enter your promo code"
                    value={betaInfo.promoCode}
                    onChange={handleBetaInfoChange}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="modal-btn-secondary" onClick={() => {
              setFile(null);
              setJobTitle('');
              // Reset file input by accessing the DOM element
              const fileInput = document.getElementById('streamline-file-input');
              if (fileInput) fileInput.value = '';
            }}>
              Clear
            </button>
            <button type="submit" className="modal-btn-primary">
              Continue
              <svg xmlns="http://www.w3.org/2000/svg" className="file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StreamlineModal; 